<script setup lang="ts">
import _ from "lodash";

import type { IEvent, IPlaylist, IPlaylistItem, ITag } from "@/types";
import type { Toast } from "~/common/components/R/Toast/Toast.vue";
import type { IPixellotPlayerSourceType } from "~~/modules/pixellot-sdk";
import { getAngleLabelFromSportAngles, getAvailableAngleNamesFromTags } from "~/videos/helpers/format-angles";

const props = defineProps<{
  event?: IEvent;
  playlist?: null | IPlaylist;
  tags: ITag[] | IPlaylistItem[];
  isClipFromPlaylist?: boolean;
}>();
const emit = defineEmits<(event: "close") => void>();

const { t } = useI18n();
const toast = useToast();
const segment = useSegment();

const options = ref({
  name: "",
  music: "https://content.you.pixellot.tv/static/internal/clipping/soundtrack/electronic.mp3",
  keep_audio: false,
  add_music: true,
  add_cross_fade: false,
  add_player_number: false,
  angle: null as IPixellotPlayerSourceType | null,
});
const sportType = computed(() => props.event?.sport_type || null);
const successToastConfig = ref<Toast>({ type: "success", message: t("pages.editor.add_clip_toast_success_text") });
const activePlayerNumber = computed(() => props.tags[0].playerNumber);
const hasTagsForSamePlayer = computed(() => [...props.tags].every((t) => t.playerNumber && t.playerNumber === activePlayerNumber.value));
const selectedClipType = ref<"clip" | "highlight">("clip");
const isHighlightTypeSelected = computed(() => selectedClipType.value === "highlight");
const isCrossFadeOptionDisabled = computed(() => props.tags.length <= 1);
const { state: musicOptions, isLoading: isLoadingMusicOptions } = useAsyncState(() => getEventClipMusicOptions(), []);
const localizedMusicOptions = computed(() =>
  musicOptions.value.map((o) => ({
    ...o,
    label: t(`labels.${o.label}`),
  }))
);

const anglesOptions = computed(() => {
  const angleNames = getAvailableAngleNamesFromTags(props.tags as IPlaylistItem[]);
  return angleNames.map((name) => ({ label: getAngleLabelFromSportAngles(name, anglesBySportType.value || []), value: name }));
});
const isMultiAngle = computed(() => anglesOptions.value.length > 1);
const { state: anglesBySportType } = useAsyncState(sportType.value ? getCachedAnglesBySportType(sportType.value) : Promise.resolve(null), null, {
  immediate: true,
});

const { isLoading, execute: onConfirm } = useAsyncState(
  () => {
    const payload = {
      tags: isMultiAngle.value ? filterTagsBySelectedAngles(props.tags as IPlaylistItem[], options.value.angle) : props.tags,
      name: options.value.name,
      soundtrack: options.value.add_music ? musicOptions.value.find((o) => o.value === options.value.music)?.label || "electronic" : "",
      sourceAudioVolume: options.value.keep_audio ? 1 : 0,
    } as const;

    if (payload.tags.length === 0) {
      if (isMultiAngle.value) return Promise.reject(new Error(t("pages.editor.no_tags_for_highlight_for_selected_angles")));
      return Promise.reject(new Error(t("pages.editor.no_tags_for_highlight")));
    }

    segment.track(
      "Create Clip from Tags",
      formatTrackClipFromTags(
        payload.name,
        payload.tags.length,
        payload.soundtrack,
        payload.sourceAudioVolume === 1,
        options.value.add_cross_fade,
        props.event ? [props.event.id] : Array.from(new Set((props.tags as IPlaylistItem[]).map((t) => t.playlist_source.eventId)))
      )
    );

    if (props.isClipFromPlaylist && props.playlist) {
      return createEventClipFromPlaylistWithOptions(props.playlist, {
        ...payload,
        transition: options.value.add_cross_fade ? "crosswarp" : null,
        angle: options.value.angle || undefined,
      });
    }

    if (isHighlightTypeSelected.value && props.event) {
      return createEventPlayerHighlightFromTagsWithOptions(props.event, {
        ...payload,
        teamColor: "#233C5F", // TODO: Pass the correct team color when will have it from BE.
        playerNumber: activePlayerNumber.value as string,
        transition: options.value.add_cross_fade ? "crossfade" : null,
      });
    }

    if (props.event) {
      return createEventClipFromTagsWithOptions(props.event, {
        ...payload,
        transition: options.value.add_cross_fade ? "crosswarp" : null,
      });
    }

    return Promise.reject(new Error(t("errors.unknown_error")));
  },
  null,
  { immediate: false, onSuccess, onError }
);
const { toggle: togglePlayingMusic, pause: pausePlayingMusic, isPlaying: isPlayingMusic } = useAudio(computed(() => options.value.music));
const isConfirmDisabled = computed(() => {
  const isAngleValid = isMultiAngle.value ? options.value.angle !== null : true;
  const isNameValid = isHighlightTypeSelected.value ? true : options.value.name && options.value.name.length >= 2 && options.value.name.length <= 50;

  return isLoading.value || !isNameValid || !isAngleValid;
});

function filterTagsBySelectedAngles(tags: IPlaylistItem[], selectedAngle: IPixellotPlayerSourceType | null): IPlaylistItem[] {
  if (!selectedAngle) return tags;

  const tagsWithoutAngles: IPlaylistItem[] = [];
  const filteredTags: IPlaylistItem[] = [];

  for (const tag of tags) {
    // Early continue if tag doesn't have playlist_source
    if (!("playlist_source" in tag && tag.playlist_source?.urls instanceof Map)) continue;

    const anglesData = new Map(tag.playlist_source.urls); // Clone to avoid mutation

    // Remove unselected angles
    for (const angleName of anglesData.keys()) {
      if (selectedAngle !== angleName) {
        anglesData.delete(angleName);
      }
    }

    if (anglesData.size === 0) {
      tagsWithoutAngles.push(tag);
    } else {
      filteredTags.push({ ...tag, playlist_source: { ...tag.playlist_source, urls: anglesData } }); // Create new tag object instead of mutating original
    }
  }

  // If there are tags without angles, show a proper warning message
  if (tagsWithoutAngles.length) {
    if (tagsWithoutAngles.length === 1) {
      const tag = tagsWithoutAngles[0];
      const missedAngles = [];

      for (const key of tag.playlist_source.urls.keys()) {
        if (selectedAngle !== key) {
          missedAngles.push(key);
        }
      }

      successToastConfig.value = {
        type: "warning",
        message: t("pages.editor.angle_not_found_warning", { tagName: tag?.title, angleName: missedAngles.join(", ") }),
        icon: "check",
      };
    } else {
      successToastConfig.value = {
        type: "warning",
        message: t("pages.editor.tags_without_angles_skipped_for_highlight"),
        icon: "check",
      };
    }
  } else {
    successToastConfig.value = { type: "success", message: t("pages.editor.add_clip_toast_success_text") };
  }

  return filteredTags;
}

function onHighlightTypeSelected() {
  successToastConfig.value = { type: "success", message: t("pages.editor.add_user_highlight_toast_success_text") };
}
function onClose() {
  pausePlayingMusic();
  emit("close");
}
function onSuccess() {
  const { type, message } = successToastConfig.value;
  toast[type](message, t("pages.editor.add_highlight_toast_success"));
  pausePlayingMusic();
  emit("close");

  if (props.event) {
    segment.track("Highlight From Tags Create Highlight", {
      ...formatTrackEvent(props.event),
      ...{ highlightType: isHighlightTypeSelected.value ? "player-highlight" : "clip" },
    });
  }
}
function onError(err: unknown) {
  toast.error(parseErrorMessage(err, { t }));

  if (props.event) {
    segment.track("Highlight Generation Failure", {
      ...formatTrackEvent(props.event),
      ...{ highlightType: isHighlightTypeSelected.value ? "player-highlight" : "clip" },
    });
  }
}

watch(isMultiAngle, (multiAngle) => {
  if (!multiAngle) {
    successToastConfig.value = { type: "success", message: t("pages.editor.add_clip_toast_success_text") };
  }
});
</script>

<template>
  <UModal
    appear
    :model-value="true"
  >
    <div class="flex items-center justify-end p-4 pb-3">
      <UButton
        title="Close"
        variant="link-secondary"
        icon="close"
        class="size-5"
        @click="onClose"
      />
    </div>

    <div class="md:px-10 md:pb-10">
      <h2
        id="modalHeading"
        class="text-2xl font-bold text-black dark:text-white"
      >
        {{ t('pages.editor.add_highlight') }}
      </h2>

      <div class="flex flex-col">
        <div class="flex flex-col gap-6 pt-6 text-black dark:text-white">
          <div
            v-if="!isClipFromPlaylist"
            class="flex flex-col gap-6 pt-6"
          >
            <p class="text-sm font-bold">
              {{ t("labels.type") }}
            </p>

            <URadio
              v-model="selectedClipType"
              name="type"
              :label="t('pages.editor.add_highlight')"
              value="clip"
            />
            <URadio
              v-model="selectedClipType"
              block
              name="type"
              value="highlight"
              :disabled="!hasTagsForSamePlayer"
              :label="hasTagsForSamePlayer ? t('pages.editor.create_athlete_highlight_for_number') : t('pages.editor.create_athlete_highlight')"
              @click="onHighlightTypeSelected"
            >
              <template #label="{ label }">
                <div class="flex grow items-center justify-between gap-3">
                  <div class="flex items-center gap-1">
                    <span>{{ label }}</span>
                    <UIcon
                      name="t-shirt"
                      class="inline-block text-green-500"
                    />
                    <span v-if="hasTagsForSamePlayer">#{{ activePlayerNumber }}</span>
                  </div>
                  <UIcon
                    v-tooltip="hasTagsForSamePlayer ? t('pages.editor.create_highlight_explanation') : t('pages.editor.create_athlete_highlight_from_same_tags_tip')"
                    name="info"
                  />
                </div>
              </template>
            </URadio>

            <UDivider lighter />
          </div>

          <UFormGroup
            :label="t('pages.editor.highlight_name')"
            name="highlight_name"
          >
            <UInput
              v-model="options.name"
              :placeholder="t('pages.editor.highlight_name_placeholder')"
              :disabled="isHighlightTypeSelected"
            />
          </UFormGroup>

          <UDivider lighter />

          <div v-if="props.isClipFromPlaylist && isMultiAngle">
            <p class="mb-6 text-sm font-bold">
              {{ t('labels.angle') }}
            </p>

            <UFormGroup
              class="mb-4"
              name="select_angles"
            >
              <USelectMenu
                v-model="options.angle"
                :disabled="!anglesOptions"
                :options="anglesOptions"
                :placeholder="t('pages.editor.angle_placeholder')"
              />
            </UFormGroup>

            <p class="mb-6 text-xs text-secondary dark:text-[#A9B7D1]">
              {{ t('pages.editor.angles_explanation') }}
            </p>

            <UDivider lighter />
          </div>

          <p class="text-sm font-bold">
            {{ t("labels.sound_options") }}
          </p>

          <div class="flex items-center justify-between">
            <label
              class="flex items-center gap-3"
              for="checkbox-clip-create-keep-audio"
            >
              <UIcon name="sound-on" />
              <span class="text-sm font-medium">{{ t("labels.keep_recorded_audio") }}</span>
            </label>
            <UCheckbox
              v-model="options.keep_audio"
              name="checkbox-clip-create-keep-audio"
            />
          </div>

          <div class="flex items-center justify-between">
            <label
              class="flex items-center gap-3"
              for="checkbox-clip-create-add-music"
            >
              <UIcon name="music" />
              <span class="text-sm font-medium">{{ t("labels.add_bg_music") }}</span>
            </label>
            <UCheckbox
              v-model="options.add_music"
              name="checkbox-clip-create-add-music"
            />
          </div>

          <USelectMenu
            v-model="options.music"
            :disabled="!options.add_music || isLoadingMusicOptions"
            :options="localizedMusicOptions"
            :leading-icon="{ name: isPlayingMusic ? 'pause' : 'play-circle', onClick: () => togglePlayingMusic() }"
          />

          <UDivider lighter />

          <p class="text-sm font-bold">
            {{ t("labels.graphic_options") }}
          </p>

          <div class="flex items-center justify-between">
            <label
              class="flex items-center gap-3"
              for="checkbox-clip-create-add-cross-fade"
              :class="{ 'pxl-disabled': isCrossFadeOptionDisabled }"
            >
              <UIcon name="crossfade" />
              <span class="text-sm font-medium">{{ t("labels.crossfade_between_cuts") }}</span>
            </label>
            <UIcon
              v-if="isCrossFadeOptionDisabled"
              v-tooltip="t('pages.editor.crossfade_option_disabled_explanation')"
              name="info"
            />
            <UCheckbox
              v-else
              v-model="options.add_cross_fade"
              name="checkbox-clip-create-add-cross-fade"
            />
          </div>
        </div>

        <UButtonGroupConfirmCancel
          class="mt-6"
          cancel-variant="secondary"
          :cancel-label="t('labels.cancel')"
          :confirm-disabled="isConfirmDisabled"
          :confirm-label="t('pages.editor.add_highlight')"
          :on-cancel-click="onClose"
          :on-confirm-click="onConfirm"
        />
      </div>
    </div>
  </UModal>
</template>
